import React, { useEffect } from 'react';
import { DelayType, MockService, ResponseDelay } from '../../../../../types/rest/mockServiceTypes';
import Waves from '../Waves/Waves';
import './BootingScreen.css';
import clsx from 'clsx';
import { useIsMount } from '../../../../../hooks/useIsMountHook';
import BootingValue from './BootingValue';
import _ from 'lodash';
import StopWatch from '../StopWatch/StopWatch';
import { DeploymentStageStatus, getBootingHeaderText } from './bootingScreenUtils';

interface VirtualServiceBootingScreenProps {
  mockService: MockService;
}

const BootingScreen = (props: VirtualServiceBootingScreenProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const isMount = useIsMount();

  useEffect(() => {
    if (isMount) {
      //Make it based on status change
      slideDown();
    }
  }, []);

  //Slide down with delay (otherwise animation will not work)
  const slideDown = async () => {
    await new Promise(resolve => setTimeout(resolve, 100));
    setExpanded(true);
  };

  const getTransactionsValue = () => {
    const txnCount = _.size(props.mockService.mockServiceTransactions);
    return (
      <BootingValue
        value={txnCount}
        showSeparator={false}
        title={txnCount === 1 ? 'Transaction' : 'Transactions'}
        variant={'transactions'}
      />
    );
  };

  const getSslValue = () => {
    // SSL Auth (enabled / disabled)
    // const serverSslAssets = _.find(props.mockService.assets, asset => asset.assetUsageType.startsWith('SERVER_'));
    // const isSsl = _.size(serverSslAssets) !== 0;
    const isSsl = props.mockService.endpointPreference === 'HTTPS';
    return <BootingValue value={'SSL'} title={isSsl ? 'Enabled' : 'Disabled'} showSeparator={true} variant={'ssl'} />;
  };

  const getBlazeDataValue = () => {
    const isBlazeDataEnabled = props.mockService.metadata?.badges?.CONTAINS_EXPRESSIONS === true;
    return (
      <BootingValue
        value={'Service Data'}
        title={isBlazeDataEnabled ? 'Active' : 'Inactive'}
        showSeparator={true}
        variant={'blazeData'}
      />
    );
  };

  const getThinkTimeValue = () => {
    const responseDelay: ResponseDelay = props.mockService.responseDelay;
    let millis: string | number = 0;

    if (!_.isNil(responseDelay)) {
      switch (responseDelay.type) {
        case DelayType.NONE:
          millis = 0;
          break;
        case DelayType.FIXED:
          millis = responseDelay.duration;
          break;
        case DelayType.UNIFORM:
          millis = `${responseDelay.lower} - ${responseDelay.upper}`;
          break;
        case DelayType.LOGNORMAL:
          millis = `${responseDelay.median}`;
          break;
      }
    }

    return <BootingValue value={`${millis} ms`} showSeparator={true} title={'Think Time'} variant={'thinkTime'} />;
  };

  const getNoMatchingValue = () => {
    const noMatchingPreference = _.get(props.mockService, 'noMatchingRequestPreference');
    let text: any = 'Return 404';
    if (noMatchingPreference !== 'return404') {
      text = (
        <span>
          <span className="icon-bzm-arrow-right mr-2"></span>
          {props.mockService.liveSystemHost}
        </span>
      );
    }

    return <BootingValue value={'No Matching'} title={text} showSeparator={true} variant={'noMatchingBehaviour'} />;
  };

  const getHeader = (deploymentStatus: DeploymentStageStatus) => {
    const headerTexts = getBootingHeaderText(deploymentStatus, props.mockService);
    return (
      <div className={'text-center'}>
        <h1 className={'font-weight-light mb-4'}>{headerTexts.title}</h1>
        <p className={'font-weight-semi-bold font-size-lg'}>{headerTexts.text}</p>
      </div>
    );
  };

  const deploymentStatus: DeploymentStageStatus = _.get(props.mockService, 'metaData.trackingStatus', 'STARTING');

  const countStopWatchDelay = () => {
    //Get startTime timestamp
    let millis = 0;
    const startTimeTs = _.get(props.mockService, 'metadata.stages.STARTING', null);
    if (!_.isNil(startTimeTs)) {
      try {
        const timestampDate = new Date(startTimeTs);
        const now = new Date();
        millis = now.getTime() - timestampDate.getTime();
      } catch (error) {
        millis = 0;
        console.error('Cannot parse timestamp', error);
      }
    }
    return millis;
  };

  return (
    <div className={clsx('expandable', expanded ? 'expanded' : undefined)}>
      <StopWatch initialDelay={countStopWatchDelay()} isRunning={deploymentStatus !== 'FINISHED'} />
      {getHeader(deploymentStatus)}
      <Waves status={deploymentStatus} />
      <div className={'d-flex bootingScreenStats my-5'}>
        <div className={'bootingScreenBox'}>{getTransactionsValue()}</div>
        <div className={'bootingScreenBox'}>{getSslValue()}</div>
        <div className={'bootingScreenBox'}>{getThinkTimeValue()}</div>
        <div className={'bootingScreenBox'}>{getBlazeDataValue()}</div>
        <div className={'bootingScreenBox'}>{getNoMatchingValue()}</div>
      </div>
      <hr />
    </div>
  );
};

export default BootingScreen;
